import {
  Box,
  Burger,
  BurgerProps,
  Group,
  ScrollArea,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core'
import { useClickOutside, useMediaQuery } from '@mantine/hooks'
import { navigationConfig } from 'config/navigationConfig'
import type { CSSProperties } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { IconType } from 'react-icons'
import { To } from 'react-router-dom'
import { useAuthStore } from 'store/authStore'
import { useCustomizerActions, useCustomizerStore } from 'store/customizerStore'
import { NavbarToggle } from '../common/NavbarToggle'
import { LinkItem } from './LinkItem'
import { LinkCollapse, LinksGroup } from './LinksGroup'
import { SidebarFooter } from './SidebarFooter'
import { SidebarHeader } from './SidebarHeader'
import { WorkspacePicker } from './WorkspacePicker'
import hamburgerClasses from './SidebarHamburger.module.css'
import classes from './VerticalSidebar.module.css'

export type NavigationItem = { label: string; permissionId?: string } & (
  | {
      type: 'link'
      icon: IconType
      href: To
    }
  | {
      type: 'divider'
    }
  | {
      type: 'linkGroup'
      icon: IconType
      links: LinkCollapse[]
    }
)

export const SidebarHamburger = (
  props: Omit<BurgerProps, 'opened' | 'onClick' | 'aria-label' | 'sx' | 'ref'>,
) => {
  const { className, ...rest } = props
  const mobileMenuOpened = useCustomizerStore(state => state.mobileMenuOpened)
  const { toggleMobileMenu } = useCustomizerActions()
  const label = mobileMenuOpened ? 'Close navigation' : 'Open navigation'

  return (
    <Burger
      color={mobileMenuOpened ? 'white' : undefined}
      className={[hamburgerClasses.hamburger, className]
        .filter(Boolean)
        .join(' ')}
      opened={mobileMenuOpened}
      onClick={() => toggleMobileMenu()}
      aria-label={label}
      {...rest}
    />
  )
}

export const VerticalSidebar = () => {
  const { t } = useTranslation()
  const isSidebarCollapsed = useCustomizerStore(state => state.sidebarCollapsed)
  const selectedWorkspace = useCustomizerStore(state => state.selectedWorkspace)
  const mobileMenuOpened = useCustomizerStore(state => state.mobileMenuOpened)
  const { toggleMobileMenu } = useCustomizerActions()
  const userPermissions = useAuthStore(state => state.user?.permissions)
  const theme = useMantineTheme()
  const isSmallScreen = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const isNavigationCollapsed = isSidebarCollapsed && !mobileMenuOpened
  const isDark = theme.colorScheme === 'dark'

  const navbarRef = useClickOutside(() => {
    if (!isSmallScreen || !mobileMenuOpened) return
    toggleMobileMenu()
  }, ['pointerdown'])

  const sidebarStyle = {
    '--vertical-sidebar-background': isDark
      ? 'linear-gradient(180deg, #111214 0%, #0d0e10 100%)'
      : 'linear-gradient(180deg, #3f465f 0%, #3a4058 100%)',
    '--vertical-sidebar-border-color': isDark
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(21, 27, 44, 0.2)',
    '--vertical-sidebar-shadow': isDark
      ? '10px 0 28px rgba(0, 0, 0, 0.22)'
      : '8px 0 28px rgba(15, 23, 42, 0.12)',
    '--vertical-sidebar-brand-color': theme.colors.brand[5],
    '--sidebar-divider-color': isDark
      ? 'rgba(214, 218, 226, 0.58)'
      : 'rgba(229, 233, 242, 0.58)',
    '--sidebar-link-text-color': isDark ? '#d6dae2' : '#e5e9f2',
    '--sidebar-link-sublink-color': isDark ? theme.white : '#f3f5fa',
    '--sidebar-link-sublink-border-color': 'rgba(255, 255, 255, 0.22)',
    '--sidebar-link-hover-color': theme.white,
    '--sidebar-link-hover-background': isDark
      ? 'rgba(255, 255, 255, 0.06)'
      : 'rgba(255, 255, 255, 0.1)',
    '--sidebar-link-hover-border-color': isDark
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(255, 255, 255, 0.16)',
    '--sidebar-link-active-color': '#151512',
    '--sidebar-link-active-background': theme.colors.brand[6],
    '--sidebar-link-active-border-color': theme.colors.brand[6],
    '--sidebar-link-active-accent': isDark
      ? theme.colors.brand[5]
      : theme.colors.brand[8],
  } as CSSProperties

  const navbarClassName = [
    classes.navbar,
    isNavigationCollapsed && classes.collapsed,
    mobileMenuOpened && classes.mobileOpened,
  ]
    .filter(Boolean)
    .join(' ')

  const items = useMemo(
    () => navigationConfig.get(selectedWorkspace)!(t),
    [t, selectedWorkspace],
  )

  const links = useMemo(
    () =>
      items
        .filter(
          item =>
            !item.permissionId || userPermissions?.includes(item.permissionId),
        )
        .map((item, i) => {
          switch (item.type) {
            case 'divider':
              return (
                <Box
                  key={item.label + '-divider'}
                  className={classes.divider}
                  aria-hidden="true"
                />
              )
            case 'link':
              return <LinkItem {...item} key={i + '-' + item.label + '-link'} />
            default:
              return (
                <LinksGroup {...item} key={i + '-' + item.label + '-group'} />
              )
          }
        }),
    [userPermissions, selectedWorkspace, items],
  )

  return (
    <Box
      ref={navbarRef}
      p="xl"
      className={navbarClassName}
      style={sidebarStyle}
    >
      <Box mb="sm" className={classes.header}>
        <Group
          mb="sm"
          wrap="nowrap"
          justify="space-between"
          className={classes.headerControls}
        >
          <SidebarHeader />
          {isSmallScreen ? <SidebarHamburger /> : <NavbarToggle />}
        </Group>
        <WorkspacePicker />
      </Box>

      <ScrollArea className={classes.links} type="auto" style={{ flex: 1 }}>
        <Stack gap={'sm'} py={0} className={classes.linksInner}>
          {links}
        </Stack>
      </ScrollArea>
      <Box className={classes.footer} mt="sm">
        <SidebarFooter />
      </Box>
    </Box>
  )
}
